import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { formatExternalLoanTerm } from '../../services/externalLoanDataProvider';
import { formatDateTimeEAT } from '../../shared/formatDateTimeEAT';
import { externalLoanButtonSx, externalLoanCardSx, externalLoanNestedCardSx } from './externalLoanCardStyles';

const brand = '#800b33';
const currencyFmt = (value) => new Intl.NumberFormat('en-TZ', { maximumFractionDigits: 0 }).format(Number(value || 0));

const Field = ({ label, value }) => (
  <Box sx={{ p: 1.5, ...externalLoanNestedCardSx }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
    <Typography variant="body2" fontWeight={800}>{value || '-'}</Typography>
  </Box>
);

const ContractPreview = ({ contract, memberProfile, groupProfile, loanSummary, guarantors = [] }) => {
  const [open, setOpen] = useState(false);
  const approvedGuarantors = guarantors.filter((guarantor) => guarantor.approved).length;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          ...externalLoanCardSx,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Digital Contract</Typography>
            <Typography variant="body2" color="text.secondary">
              Contract status and preview for the selected external loan application.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => setOpen(true)} sx={externalLoanButtonSx}>
              View Contract
            </Button>
            <Button variant="contained" startIcon={<DownloadOutlinedIcon />} sx={{ ...externalLoanButtonSx, bgcolor: brand }}>
              Download Contract
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ mt: 2, p: 2, ...externalLoanNestedCardSx }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label="Contract reference" value={contract.reference} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label="Borrower" value={`${memberProfile.memberName} - ${groupProfile.name}`} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label="Requested amount" value={`TZS ${currencyFmt(loanSummary.requestedAmount)}`} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Contract status</Typography>
                <Chip label={contract.status} color={contract.status === 'Signed' ? 'success' : contract.status === 'Archived' ? 'default' : 'warning'} size="small" sx={{ alignSelf: 'flex-start' }} />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle sx={{ color: brand, fontWeight: 900 }}>
          Bellicks Finance External Loan Contract
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={900}>Bellicks Finance Limited</Typography>
              <Typography variant="body2" color="text.secondary">
                In partnership with Savvy Community for group-backed external financing.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><Field label="Contract Reference" value={contract.reference} /></Grid>
              <Grid item xs={12} md={4}><Field label="Borrower" value={memberProfile.memberName} /></Grid>
              <Grid item xs={12} md={4}><Field label="Group" value={groupProfile.name} /></Grid>
              <Grid item xs={12} md={4}><Field label="Requested Amount" value={`TZS ${currencyFmt(loanSummary.requestedAmount)}`} /></Grid>
              <Grid item xs={12} md={4}><Field label="Approved Amount" value={`TZS ${currencyFmt(loanSummary.approvedAmount || loanSummary.requestedAmount)}`} /></Grid>
              <Grid item xs={12} md={4}><Field label="Term" value={formatExternalLoanTerm(loanSummary)} /></Grid>
              <Grid item xs={12} md={4}><Field label="Repayment Frequency" value={loanSummary.repaymentFrequency} /></Grid>
              <Grid item xs={12} md={4}><Field label="Interest / Charges" value={`TZS ${currencyFmt(Number(loanSummary.interest || 0) + Number(loanSummary.fees || 0))}`} /></Grid>
              <Grid item xs={12} md={4}><Field label="Total Payable" value={`TZS ${currencyFmt(loanSummary.totalPayable)}`} /></Grid>
              <Grid item xs={12} md={4}><Field label="Guarantor Approvals" value={`${approvedGuarantors} of ${guarantors.length} approved`} /></Grid>
              <Grid item xs={12} md={4}><Field label="Contract Status" value={contract.status} /></Grid>
              <Grid item xs={12} md={4}><Field label="Generated" value={formatDateTimeEAT(contract.generatedAt)} /></Grid>
            </Grid>

            <Divider />

            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={900}>Contract Terms Preview</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                This simulation preview represents a group-backed external loan agreement between Bellicks Finance Limited,
                Savvy Community, and {memberProfile.memberName}. The borrower agrees to repay the approved amount,
                interest, and charges through the configured repayment frequency. The system generates this contract
                before Chairperson, Secretary, and Accountant approvals.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Contract status: {contract.status}. Notes: {contract.notes}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadOutlinedIcon />}>Download Contract</Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContractPreview;
